import { Injectable, InjectionToken, Signal, computed, inject } from '@angular/core';
import { HdGateInfo, HdGateInfoService, HdGateCategoryEntry } from '../hd-gate-info.service';
import { HdGateCategory } from '../hd-gate-categories';
import { HdZodiacSign } from '../hd-gate-coordinates';


/**
 * Injection-token voor de bron van per-poort informatie.
 *
 * MVP-implementatie ({@link DefaultHdGateInfoProvider}) leest uit de bestaande
 * `HdGateInfoService` (JSON-backed via Transloco). Latere implementaties kunnen API-data
 * en gebruiker-specifieke overrides toevoegen zonder dat UI-componenten aangepast hoeven
 * te worden — patroon analoog aan `CONFIRMER` / `NOTIFIER` elders in de codebase.
 */
export const HD_GATE_INFO_PROVIDER = new InjectionToken<HdGateInfoProvider>('HD_GATE_INFO_PROVIDER', {
  providedIn: 'root',
  factory: () => inject(DefaultHdGateInfoProvider),
});

export interface HdGateInfoProvider {
  /**
   * Reactieve gate-info: het signal moet her-emitten zodra onderliggende data wijzigt
   * (bv. taalwissel, of later: API-refresh / customization-update).
   */
  gateInfo(gate: number): Signal<HdGateInfo>;

  /**
   * Lang-geparametriseerde variant van {@link gateInfo} voor consumers met een
   * lokale taal-override (cheat-sheet). Geeft een waarde terug i.p.v. een Signal:
   * de caller leest zelf het taal-signal binnen een `computed`, zodat reactiviteit
   * op zowel taalwissel als async scope-load behouden blijft.
   */
  gateInfoIn(gate: number, lang: string): HdGateInfo;

  /** Categorie-info voor een poort (active lang); `null` als de poort niet in de categorie zit. */
  gateCategoryInfo(gate: number, category: HdGateCategory): HdGateCategoryEntry | null;

  /** Lang-geparametriseerde variant van {@link gateCategoryInfo}. */
  gateCategoryInfoIn(gate: number, category: HdGateCategory, lang: string): HdGateCategoryEntry | null;

  /** Vertaald sterrenbeeld-label (active lang); valt terug op de ruwe key. */
  zodiacLabel(sign: HdZodiacSign): string;

  /** Lang-geparametriseerde variant van {@link zodiacLabel}. */
  zodiacLabelIn(sign: HdZodiacSign, lang: string): string;

  /** Zorgt dat de (lazy) categorie-scopes voor deze poort geladen worden (active lang). */
  loadGateCategoryScopesForGate(gate: number): void;

  /** Lang-geparametriseerde variant van {@link loadGateCategoryScopesForGate}. */
  loadGateCategoryScopesForGateIn(gate: number, lang: string): void;
}

/**
 * Default-implementatie: dunne adapter rond `HdGateInfoService`.
 *
 * `HdGateInfoService.gateInfo(gate)` is al reactief (leest signals voor translations en scope-loads),
 * maar geeft een waarde terug i.p.v. een signal. We wrappen het in een `computed` zodat de provider
 * een uniform `Signal<HdGateInfo>`-contract kan aanbieden.
 */
@Injectable({ providedIn: 'root' })
export class DefaultHdGateInfoProvider implements HdGateInfoProvider {
  private readonly source = inject(HdGateInfoService);

  gateInfo(gate: number): Signal<HdGateInfo> {
    return computed(() => this.source.gateInfo(gate));
  }

  gateInfoIn(gate: number, lang: string): HdGateInfo {
    return this.source.gateInfoIn(gate, lang);
  }

  gateCategoryInfo(gate: number, category: HdGateCategory): HdGateCategoryEntry | null {
    return this.source.gateCategoryInfo(gate, category);
  }

  gateCategoryInfoIn(gate: number, category: HdGateCategory, lang: string): HdGateCategoryEntry | null {
    return this.source.gateCategoryInfoIn(gate, category, lang);
  }

  zodiacLabel(sign: HdZodiacSign): string {
    return this.source.zodiacLabel(sign);
  }

  zodiacLabelIn(sign: HdZodiacSign, lang: string): string {
    return this.source.zodiacLabelIn(sign, lang);
  }

  loadGateCategoryScopesForGate(gate: number): void {
    this.source.loadGateCategoryScopesForGate(gate);
  }

  loadGateCategoryScopesForGateIn(gate: number, lang: string): void {
    this.source.loadGateCategoryScopesForGateIn(gate, lang);
  }
}
